import { useCallback, useEffect, useMemo, useState } from 'react'

import { useMutation } from '@apollo/client'
import { ActionIcon, Avatar, Menu, TextInput, Tooltip } from '@mantine/core'
import { useDebouncedValue, useDisclosure } from '@mantine/hooks'
import dayjs from 'dayjs'

import { useQuery } from '@redwoodjs/web'

import DNButton from 'src/components/Buttons/Button/DNButton'
import DnTable from 'src/components/Core/DnTable'
import Empty from 'src/components/Feedback/Empty/Empty'
import DeleteModal from 'src/components/Overlays/DeleteModal/DeleteModal'
import { toast } from 'src/components/Overlays/Toast/Toast'
import {
  DELETE_TEXT_TEMPLATE,
  GET_TEXT_TEMPLATES,
  GET_TEXT_TEMPLATES_WITH_CREATOR,
} from 'src/graphql/textTemplates.graphql'
import IconCopy from 'src/icons/IconCopy'
import IconSearch from 'src/icons/IconSearch'
import IconThreeDots from 'src/icons/IconThreeDots'
import { buildFullName } from 'src/lib/formatters'

import CreateTemplateModal from './components/CreateTemplateModal'

const onSuccess = () => {
  toast('Your text template was deleted successfully', 'success')
}

const TextTemplateTab = () => {
  const [selectedTemplate, setSelectedTemplate] = useState(null)
  const [editing, setEditing] = useState(false)
  const [createModalOpened, createModalHandlers] = useDisclosure(false)
  const [deleteModalOpened, deleteModalHandlers] = useDisclosure(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [debouncedSearchTerm] = useDebouncedValue(searchTerm, 300)
  const defaultResultsPerPage = 20

  const onError = useCallback(() => {
    toast('Something went wrong, please try again.', 'error')
  }, [])

  //QUERIES:
  const {
    data: textTemplatesList,
    loading: textTemplatesListLoading,
    refetch,
  } = useQuery(GET_TEXT_TEMPLATES_WITH_CREATOR, {
      variables: {
        pageInfo: {
          limit: defaultResultsPerPage,
          offset: 0,
        },
        orderBy: {
          field: 'title',
          direction: 'asc',
        },
        search: debouncedSearchTerm || undefined,
      },
      onError,
    })

  const handleLoadPage = useCallback(
    ({ offset, limit }: { offset: number; limit: number }) => {
      refetch({
        pageInfo: {
          limit,
          offset,
        },
        orderBy: {
          field: 'title',
          direction: 'asc',
        },
        search: debouncedSearchTerm || undefined,
      })
    },
    [refetch, debouncedSearchTerm]
  )

  //MUTATIONS:
  // Both documents are refetched: this list reads the WITH_CREATOR query, but
  // the SMS pickers read the slimmer one, and a template added or removed here
  // has to show up there too.
  const [deleteTextTemplate] = useMutation(DELETE_TEXT_TEMPLATE, {
    refetchQueries: [GET_TEXT_TEMPLATES_WITH_CREATOR, GET_TEXT_TEMPLATES],
    onCompleted: () => {
      onSuccess()
    },
    onError,
  })

  //HANDLERS:
  const onEdit = (element) => {
    setSelectedTemplate(element)
    setEditing(true)
    createModalHandlers.open()
  }

  const onCreate = () => {
    setSelectedTemplate(undefined)
    setEditing(false)
    createModalHandlers.open()
  }

  const onDelete = () => {
    deleteTextTemplate({
      variables: {
        textTemplateId: selectedTemplate.textTemplateId,
      },
    })
  }

  // This resets the selected template to null when the modal is closed
  //  - Creating a new template sets the selected template to undefined
  //  - Editing a template sets the selected template to the template being edited
  // Note: This distinction is important in order to reset the CreateTemplateModal's form
  useEffect(() => {
    if (!createModalOpened) {
      setSelectedTemplate(null)
    }
  }, [createModalOpened])

  const templates = textTemplatesList?.textTemplates?.items ?? []
  const totalCount = textTemplatesList?.textTemplates?.totalCount ?? 0

  const columns = useMemo(
    () => [
      {
        key: 'textTemplateId',
        label: 'ID',
        width: 6,
        render: (template) => (
          <Tooltip label={template.textTemplateId} withArrow>
            <ActionIcon
              variant="subtle"
              size={14}
              color="gray"
              onClick={(e) => {
                e.stopPropagation()
                navigator?.clipboard?.writeText?.(template.textTemplateId)
              }}
            >
              <IconCopy className="fill-none stroke-doubleNickel-gray-500" />
            </ActionIcon>
          </Tooltip>
        ),
      },
      {
        key: 'title',
        label: 'Template Name',
        width: 30,
        render: (template) => template.title,
      },
      {
        key: 'createdBy',
        label: 'Created By',
        width: 10,
        render: (template) =>
          template.createdBy ? (
            <Tooltip label={buildFullName(template.createdBy)} withArrow>
              <Avatar color="blue" size="sm" radius="sm">
                {template.createdBy.firstName?.charAt(0)}
                {template.createdBy.lastName?.charAt(0)}
              </Avatar>
            </Tooltip>
          ) : (
            'N/A'
          ),
      },
      {
        key: 'createdAt',
        label: 'Created Date',
        render: (template) => (
          <Tooltip
            label={dayjs(template.createdAt).format('MM/DD/YYYY h:mm A')}
            withArrow
          >
            <span>{dayjs(template.createdAt).format('MM/DD/YYYY')}</span>
          </Tooltip>
        ),
      },
      {
        key: 'updatedAt',
        label: 'Updated Date',
        render: (template) => (
          <Tooltip
            label={dayjs(template.updatedAt).format('MM/DD/YYYY h:mm A')}
            withArrow
          >
            <span>{dayjs(template.updatedAt).format('MM/DD/YYYY')}</span>
          </Tooltip>
        ),
      },
      {
        key: 'actions',
        label: 'Actions',
        width: 10,
        render: (template) => (
          <div className="flex flex-row justify-end">
            <Menu shadow="md" position="bottom-end">
              <Menu.Target>
                <ActionIcon
                  variant="subtle"
                  className="h-6 w-6"
                  onClick={(e) => {
                    e.stopPropagation()
                  }}
                >
                  <IconThreeDots className="fill-doubleNickel-white stroke-doubleNickel-gray-600" />
                </ActionIcon>
              </Menu.Target>
              <Menu.Dropdown className="w-40">
                <Menu.Item
                  className="text-doubleNickel-gray-700"
                  onClick={(e) => {
                    e.stopPropagation()
                    onEdit(template)
                  }}
                >
                  Edit
                </Menu.Item>
                <Menu.Item
                  className="text-doubleNickel-gray-700"
                  onClick={(e) => {
                    e.stopPropagation()
                    setSelectedTemplate(template)
                    deleteModalHandlers.open()
                  }}
                >
                  Delete
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>
          </div>
        ),
      },
    ],
    [deleteModalHandlers, onEdit]
  )

  return (
    <div className="flex h-full flex-col gap-6">
      <div className="flex flex-row justify-between">
        <div className="flex flex-col ">
          <div className="text-lg font-semibold text-doubleNickel-gray-900">
            SMS Templates
          </div>
          <div className="text-sm text-doubleNickel-gray-600">
            {/* These spans are necessary to prevent Google Translate issues  */}
            <span>{totalCount}</span>
            <span> SMS templates</span>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <TextInput
            placeholder="Search templates..."
            leftSection={
              <IconSearch className="h-4 w-4 fill-none stroke-doubleNickel-gray-500" />
            }
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.currentTarget.value)}
            className="w-64"
          />
          <DNButton variant="primary" onClick={() => onCreate()}>
            Create Template
          </DNButton>
        </div>
      </div>
      <div className="min-h-0 flex-1">
        <DnTable
          columns={columns}
          data={templates}
          enableColumnResize={false}
          enablePagination
          loading={textTemplatesListLoading}
          totalCount={totalCount}
          onLoadPage={handleLoadPage}
          onRowClick={(_idx, row) => onEdit(row)}
          defaultResultsPerPage={defaultResultsPerPage}
          emptyStateMessage={
            <Empty title="No templates found" subtitle="Add one to get started" />
          }
        />
      </div>
      <CreateTemplateModal
        textTemplate={selectedTemplate}
        edit={editing}
        isOpen={createModalOpened}
        onClose={() => createModalHandlers.close()}
        refetchQueries={[GET_TEXT_TEMPLATES_WITH_CREATOR, GET_TEXT_TEMPLATES]}
      />
      <DeleteModal
        title={`Are you sure you want to delete the template ${selectedTemplate?.title}?`}
        opened={deleteModalOpened}
        close={deleteModalHandlers.close}
        onDelete={onDelete}
      />
    </div>
  )
}

export default TextTemplateTab
